import { useState } from "react";
import {
  Upload,
  FileText,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Loader2,
  ExternalLink,
} from "lucide-react";

/**
 * Fellowship step: the two documents a Member → Fellow candidate lodges.
 *
 *   • the citation — ONE PAGE, refused by the server if it runs longer
 *   • their CURRENT QSRBN practice licence
 *
 * Both are required to move on. The one-page rule is stated here as plainly
 * as the server enforces it, because being told after uploading is a wasted
 * trip: the candidate has to go back, re-scan and return.
 *
 * A file already lodged is shown rather than forgotten. Coming back to this
 * step to replace one document must not mean finding the other again, so an
 * untouched slot sends back the path it already holds and the server keeps it.
 */

const ACCEPT = ".pdf,.jpg,.jpeg,.png";
const MAX_MB = 5;

export type FellowshipDocumentsInitial = {
  citation_file?: string | null;
  qsrbn_licence_file?: string | null;
};

export type FellowshipDocumentsPayload = FormData;

export default function FellowshipDocumentsStep({
  onSubmit,
  onBack,
  busy,
  initialValues,
  storageBase,
  readOnly = false,
}: {
  onSubmit: (body: FormData) => void | Promise<void>;
  onBack?: () => void;
  busy?: boolean;
  initialValues?: FellowshipDocumentsInitial;
  /** Turns a stored path into something a browser can open. */
  storageBase: (path?: string | null) => string;
  readOnly?: boolean;
}) {
  const savedCitation = initialValues?.citation_file ?? "";
  const savedLicence = initialValues?.qsrbn_licence_file ?? "";

  const [citation, setCitation] = useState<File | null>(null);
  const [licence, setLicence] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const hasCitation = !!citation || !!savedCitation;
  const hasLicence = !!licence || !!savedLicence;

  const submit = () => {
    setError(null);

    // Said before the upload rather than after it. The server checks the same
    // things; this just saves a round trip on the two it can see from here.
    if (!hasCitation) {
      setError("Please upload your citation.");
      return;
    }
    if (!hasLicence) {
      setError("Please upload your current QSRBN practice licence.");
      return;
    }

    const fd = new FormData();

    if (citation) fd.append("citation_file", citation);
    else if (savedCitation) fd.append("citation_file_path", savedCitation);

    if (licence) fd.append("qsrbn_licence_file", licence);
    else if (savedLicence) fd.append("qsrbn_licence_file_path", savedLicence);

    onSubmit(fd);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Citation and practice licence</h2>
        <p className="text-sm text-gray-500 mt-1">
          Two documents are required for fellowship. Both may be a PDF or a photograph, up to{" "}
          {MAX_MB}MB each.
        </p>
      </div>

      <FileSlot
        label="Citation"
        hint="One page only — a longer file will be refused."
        accept={ACCEPT}
        file={citation}
        savedPath={savedCitation}
        storageBase={storageBase}
        disabled={readOnly || !!busy}
        onPick={setCitation}
      />

      <FileSlot
        label="QSRBN practice licence"
        hint="Your current licence. An expired one will be sent back."
        accept={ACCEPT}
        file={licence}
        savedPath={savedLicence}
        storageBase={storageBase}
        disabled={readOnly || !!busy}
        onPick={setLicence}
      />

      {error && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {!readOnly && (
        <div className="flex items-center justify-between gap-3 pt-2">
          {onBack ? (
            <button
              onClick={onBack}
              disabled={!!busy}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg border bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={submit}
            disabled={!!busy}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm font-semibold hover:from-indigo-500 hover:to-blue-500 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save and continue
            {!busy ? <ArrowRight className="w-4 h-4" /> : null}
          </button>
        </div>
      )}
    </div>
  );
}

function FileSlot({
  label,
  hint,
  accept,
  file,
  savedPath,
  storageBase,
  disabled,
  onPick,
}: {
  label: string;
  hint: string;
  accept: string;
  file: File | null;
  savedPath: string;
  storageBase: (path?: string | null) => string;
  disabled?: boolean;
  onPick: (f: File | null) => void;
}) {
  const url = savedPath ? storageBase(savedPath) : "";

  return (
    <div className="rounded-2xl border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-gray-900 text-sm">{label}</div>
          <div className="text-xs text-gray-500 mt-0.5">{hint}</div>
        </div>

        {/* What is already lodged, openable. A candidate returning to this
            step should be able to check what they sent before replacing it. */}
        {!file && url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
          >
            <FileText className="w-3.5 h-3.5" />
            View the one on file
            <ExternalLink className="w-3 h-3" />
          </a>
        ) : null}
      </div>

      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <label
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium cursor-pointer ${
            disabled
              ? "opacity-50 cursor-not-allowed bg-gray-50"
              : "bg-white hover:bg-gray-50 text-gray-700"
          }`}
        >
          <Upload className="w-4 h-4" />
          {file || savedPath ? "Replace" : "Choose file"}
          <input
            type="file"
            accept={accept}
            disabled={disabled}
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0] ?? null)}
          />
        </label>

        {file ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {file.name}
          </span>
        ) : savedPath ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Already uploaded
          </span>
        ) : (
          <span className="text-xs text-gray-400">Nothing uploaded yet</span>
        )}
      </div>
    </div>
  );
}
